import PropTypes from 'prop-types';
import { Dialog, DialogContent, Box, Typography, Button, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';

// assets
import logoLight from 'assets/images/logo-light-long.png';

export default function WelcomeTourModal({ open, onSkip, onStartTour, title, description, allowSkip = true }) {
  const theme = useTheme();
  const intl = useIntl();

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9) !important',
            backdropFilter: 'none'
          }
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.9)'
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          width: '100%',
          maxWidth: 480,
          m: { xs: 2, sm: 4 }
        }
      }}
    >
      {/* Header with gradient background */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #3d7a89 0%, #5a9aa8 50%, #7ab8c4 100%)',
          position: 'relative',
          py: { xs: 3, sm: 4 },
          px: { xs: 2.5, sm: 3 },
          textAlign: 'center',
          overflow: 'hidden'
        }}
      >
        {/* Decorative circle */}
        <Box
          sx={{
            position: 'absolute',
            top: -40,
            right: -40,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.15)'
          }}
        />

        {/* Logo */}
        <Box
          component="img"
          src={logoLight}
          alt="AYRO Logo"
          sx={{
            height: 45,
            width: 'auto',
            mb: 2,
            position: 'relative',
            zIndex: 1,
            filter: 'brightness(0) invert(1)' // Make logo white
          }}
        />

        {/* Welcome text */}
        <Typography
          variant="h4"
          sx={{
            color: '#fff',
            fontWeight: 600,
            position: 'relative',
            zIndex: 1,
            fontFamily: '"Poppins", sans-serif',
            letterSpacing: '0.5px'
          }}
        >
          {title || intl.formatMessage({ id: 'tour.welcomeTitle' })}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            mt: 1,
            position: 'relative',
            zIndex: 1
          }}
        >
          {intl.formatMessage({ id: 'tour.welcomeSubtitle' })}
        </Typography>
      </Box>

      {/* Content */}
      <DialogContent sx={{ py: { xs: 3, sm: 4 }, px: { xs: 2.5, sm: 4 } }}>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            textAlign: 'center',
            mb: { xs: 3, sm: 4 },
            lineHeight: 1.7
          }}
        >
          {description || intl.formatMessage({ id: 'tour.welcomeDescription' })}
        </Typography>

        {/* Buttons — stack full-width on mobile so "Start Tour" never wraps */}
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} justifyContent="center" alignItems="stretch">
          {allowSkip && (
            <Button
              variant="outlined"
              onClick={onSkip}
              sx={{
                px: { xs: 3, sm: 4 },
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.95rem',
                whiteSpace: 'nowrap',
                width: { xs: '100%', sm: 'auto' },
                borderColor: theme.palette.divider,
                color: theme.palette.text.secondary,
                '&:hover': {
                  borderColor: theme.palette.text.secondary,
                  backgroundColor: 'transparent'
                }
              }}
            >
              {intl.formatMessage({ id: 'tour.skip' })}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onStartTour}
            sx={{
              px: { xs: 3, sm: 4 },
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
              backgroundColor: '#3d7a89',
              '&:hover': {
                backgroundColor: '#2d6a79'
              }
            }}
          >
            {intl.formatMessage({ id: 'tour.startTour' })}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

WelcomeTourModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onSkip: PropTypes.func.isRequired,
  onStartTour: PropTypes.func.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  allowSkip: PropTypes.bool
};
